import React from 'react';

/**
 * Full-screen progress overlay for the checkout submit.
 *
 * Measured on QA, the card submit takes ~4s on the tokenize path and 11-14s on the payment-intent
 * path, almost all of it Chargebee's gateway orchestration and the subscription create. Before this
 * the button simply greyed out for that whole time, which reads as a broken page: customers
 * double-click, hit back, or refresh mid-charge.
 *
 * Deliberately shown on BOTH gateway buckets. Only showing it on the slower one would introduce a
 * UX difference between the A/B arms and pollute the very conversion numbers the test exists to
 * measure.
 *
 * Styles are scoped under .vv-progress and injected with the component rather than added to the
 * global sheets: the source mark uses generic class names (.st0/.st1/.st2) that would collide, and
 * the gradient/clip ids are namespaced for the same reason.
 */

const CSS = `
.vv-progress{
  position:fixed;inset:0;z-index:2000;
  display:flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,.94);
  padding:24px;
}
.vv-progress__panel{display:flex;flex-direction:column;align-items:center;gap:20px;text-align:center}
.vv-progress__heading{
  margin:0;font-size:19px;font-weight:600;color:#22205f;line-height:1.35;
}
.vv-progress__sub{margin:0;font-size:15px;color:#5b5b73;line-height:1.5;max-width:34ch}

.vv-progress__logo{width:100px;height:100px;overflow:visible}
.vv-progress .vv-frame{fill:#fff}
.vv-progress .vv-navy{fill:#22205f}
.vv-progress .vv-tri{fill:url(#vvProgressGradient)}
.vv-progress .vv-slash{fill:#fff}

.vv-progress .vv-mark{animation:vvMarkFade 3.8s ease-in-out infinite}
.vv-progress .vv-navy{animation:vvNavyIn 3.8s ease-in-out infinite}
.vv-progress .vv-tri{
  transform-box:fill-box;transform-origin:100% 100%;
  animation:vvTriGrow 3.8s cubic-bezier(.34,1.15,.4,1) infinite;
}
.vv-progress .vv-slash{
  transform-box:fill-box;
  animation:vvSlashIn 3.8s cubic-bezier(.4,0,.2,1) infinite;
}

@keyframes vvMarkFade{0%{opacity:0}6%{opacity:1}80%{opacity:1}100%{opacity:0}}
@keyframes vvNavyIn{0%{opacity:0}16%{opacity:1}100%{opacity:1}}
@keyframes vvTriGrow{0%{transform:scale(0)}10%{transform:scale(0)}50%{transform:scale(1)}100%{transform:scale(1)}}
@keyframes vvSlashIn{
  0%{opacity:0;transform:translate(-38px,-38px) scale(.96)}
  40%{opacity:0;transform:translate(-38px,-38px) scale(.96)}
  74%{opacity:1;transform:translate(0,0) scale(1)}
  100%{opacity:1;transform:translate(0,0) scale(1)}
}

@media (prefers-reduced-motion: reduce){
  .vv-progress .vv-mark,.vv-progress .vv-mark *{animation:none !important}
  .vv-progress .vv-mark{opacity:1}
  .vv-progress .vv-tri{transform:none}
}
`;

export const SUBMIT_PHASE = {
  AUTHORIZING: 'authorizing',
  COMPLETING: 'completing'
};

function CheckoutProgress({ phase, labels }) {
  if (!phase) {
    return null;
  }

  const isCompleting = phase === SUBMIT_PHASE.COMPLETING;
  const heading = isCompleting
    ? (labels?.completing ?? 'Completing your purchase…')
    : (labels?.authorizing ?? 'Verifying your card…');
  // The warning only appears once the charge is actually in flight. Saying "don't close this" while
  // we're still tokenising would be alarming and untrue.
  const sub = isCompleting
    ? (labels?.completingSub ?? 'Please don’t close or refresh this page.')
    : (labels?.authorizingSub ?? 'This can take a few moments.');

  return (
    <div className="vv-progress" role="status" aria-live="polite">
      <style>{CSS}</style>
      <div className="vv-progress__panel">
        <svg
          className="vv-progress__logo"
          viewBox="0 0 432 432"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="VectorVest"
        >
          <defs>
            <linearGradient
              id="vvProgressGradient"
              x1="64.7"
              y1="167.6"
              x2="264.6"
              y2="367.4"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#988058" />
              <stop offset="0.2" stopColor="#d1be8e" />
              <stop offset="0.3" stopColor="#e3d3a0" />
              <stop offset="0.4" stopColor="#efdfab" />
              <stop offset="0.5" stopColor="#f3e4af" />
              <stop offset="0.6" stopColor="#efdfab" />
              <stop offset="0.7" stopColor="#e3d3a0" />
              <stop offset="0.8" stopColor="#d1be8e" />
              <stop offset="0.9" stopColor="#b6a175" />
              <stop offset="1" stopColor="#988058" />
            </linearGradient>
            <clipPath id="vvProgressClip">
              <rect x="13.1" y="13.1" width="405.9" height="405.9" />
            </clipPath>
          </defs>

          <g className="vv-mark">
            <path className="vv-frame" d="M432,432H0V0h432v432ZM26.1,405.9h379.8V26.1H26.1v379.8Z" />
            <rect className="vv-navy" x="13.1" y="13.1" width="405.9" height="405.9" />
            <polygon className="vv-tri" points="15.1 217.3 214.5 217.3 214.5 417.4" />
            <g clipPath="url(#vvProgressClip)">
              <polygon
                className="vv-slash"
                points="120 13.2 13.1 13.2 13.1 14.5 417.7 419 419.2 419 419.2 312.4 120 13.2"
              />
            </g>
          </g>
        </svg>

        <div className="vv-progress__panel">
          <p className="vv-progress__heading">{heading}</p>
          <p className="vv-progress__sub">{sub}</p>
        </div>
      </div>
    </div>
  );
}

export default CheckoutProgress;
